/**
 * Mention text handling, shared by the composer and the bubble renderer.
 *
 * The server stores a message body as plain text — there is no mention column on
 * `chats` and no offsets on the wire — so "where are the mentions in this string"
 * has to be answered against the channel roster at paint time. Both sides of that
 * (which ids the composer sends, which spans the bubble highlights) need the same
 * notion of what counts as a match, hence one module.
 *
 * The old composer answered it with `/@(\w+\s\w+)/g`, which requires a name of
 * exactly two `\w`-separated tokens. The roster holds `first_name`, a single
 * token, so that pattern matched nothing it was pointed at — and hyphenated,
 * apostrophised and non-ASCII names never stood a chance either.
 *
 * PERFORMANCE: the roster work and the per-message work are separated. See
 * `buildMentionMatcher` — the previous single-function API re-derived the roster on
 * every call, and it is called once per text bubble per render.
 */

// What may not sit either side of a matched name. Unicode-aware, so "@José" is
// not cut short at the accent and "@Ali" does not match inside "@Alice".
const NAME_CHAR = /[\p{L}\p{N}_]/u;

/**
 * Is the `@` at `index` the start of a mention rather than part of a word?
 *
 * Without this, `dev@synapse.io` in a group containing a member named "Synapse"
 * highlighted the domain — an `@` glued to the preceding character is an address,
 * not an address-of.
 */
const startsMention = (text, index) => {
  const before = text[index - 1];
  return before === undefined || !NAME_CHAR.test(before);
};

/** Does `@name` appear in `text` as a whole token? */
export const hasMention = (text, name) => {
  if (!text || !name) return false;
  const needle = `@${name}`.toLowerCase();
  const haystack = text.toLowerCase();

  let from = 0;
  for (;;) {
    const at = haystack.indexOf(needle, from);
    if (at === -1) return false;
    const after = text[at + needle.length];
    if (startsMention(text, at) && (after === undefined || !NAME_CHAR.test(after))) return true;
    from = at + 1;
  }
};

/**
 * Prepare a roster once, then match many messages against it.
 *
 * This split is the whole point of the module's shape. `segmentMentions` used to do
 * all of this per call: allocate a `Set`, sort the roster by length, and lower-case
 * each candidate inside the scan's inner loop. It is called once per text bubble per
 * render, so in a 500-member channel with 200 messages on screen a single keystroke
 * in the composer meant 200 roster sorts and 200 × 500 `toLowerCase()` calls.
 *
 * Names are sorted longest-first so a roster holding both "Al" and "Alice"
 * highlights "@Alice" as Alice rather than as Al followed by stray characters. The
 * matched slice is returned as the author typed it, not as the roster spells it, so
 * highlighting never silently rewrites the message.
 *
 * @param {string[]} names roster display names; empty disables matching entirely
 *   (a 1:1 chat, or a secret group, where mentions do not exist)
 * @returns {(text: string) => Array<string | { text: string }>}
 */
export const buildMentionMatcher = (names) => {
  const candidates = [...new Set((names ?? []).filter(Boolean))]
    .sort((a, b) => b.length - a.length)
    // Lower-cased once per roster instead of once per comparison.
    .map((name) => ({ length: name.length, lower: name.toLowerCase() }));

  if (candidates.length === 0) {
    // No roster: every body is a single plain segment. Returned as its own branch so
    // the common 1:1 case does not enter the scan at all.
    return (text) => (text ? [text] : []);
  }

  return (text) => {
    if (!text) return [];

    const segments = [];
    // Start of the current plain run. The previous implementation accumulated it one
    // character at a time (`plain += text[i]`), which allocates a new string per
    // character; this slices once per boundary instead.
    let plainFrom = 0;
    let i = 0;

    while (i < text.length) {
      if (text[i] === '@' && startsMention(text, i)) {
        const restLower = text.slice(i + 1).toLowerCase();
        let hit;
        for (const candidate of candidates) {
          if (!restLower.startsWith(candidate.lower)) continue;
          const after = text[i + 1 + candidate.length];
          if (after === undefined || !NAME_CHAR.test(after)) {
            hit = candidate;
            break;
          }
        }

        if (hit) {
          if (i > plainFrom) segments.push(text.slice(plainFrom, i));
          segments.push({ text: text.slice(i + 1, i + 1 + hit.length) });
          i += hit.length + 1;
          plainFrom = i;
          continue;
        }
      }

      i += 1;
    }

    if (plainFrom < text.length) segments.push(text.slice(plainFrom));
    return segments;
  };
};

/**
 * Split `text` into plain strings and `{ text }` mention objects.
 *
 * Convenience wrapper over `buildMentionMatcher` for one-off calls. Anything
 * rendering a LIST of messages should build the matcher once and reuse it — see
 * `MessageText`, which accepts a prepared matcher.
 *
 * @param {string} text message body
 * @param {string[]} names roster display names
 * @returns {Array<string | { text: string }>}
 */
export const segmentMentions = (text, names) => buildMentionMatcher(names)(text);

export default { hasMention, segmentMentions, buildMentionMatcher };
